'use client';

import * as React from 'react';
import type { User } from 'firebase/auth';
import { Loader2 } from 'lucide-react';
import { submitMemberRequest } from '@/lib/actions';

const CREAM = '#f5f2ed';
const INK = '#18191B';
const SANS = "'Halyard Display', system-ui, sans-serif";
const MAX_TEXTAREA_HEIGHT = 140;

/**
 * "How can we help you?" ask bar in the front-page hero for signed-in members.
 * Deliberately minimal — one field and a button — so asking us for something
 * takes seconds. The server attaches the customer's details to the email.
 * Enter sends; Shift+Enter adds a line.
 */
export function HeroRequestBar({ user }: { user: User }) {
  const [message, setMessage] = React.useState('');
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Grow the field with its content, up to a few lines, so the bar stays one
  // line for short requests and never pushes the hero out of its frame.
  React.useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [message]);

  const send = async () => {
    if (!message.trim() || status === 'sending') return;
    setStatus('sending');
    setError(null);
    try {
      await submitMemberRequest(await user.getIdToken(), message);
      setMessage('');
      setStatus('sent');
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again, or email contact@nordicluxadvisor.com.');
      setStatus('idle');
    }
  };

  if (status === 'sent') {
    return (
      <div style={{ flex: '1 1 560px', maxWidth: 880, fontFamily: SANS }} role="status">
        <div style={{ fontFamily: "'Forum', serif", fontStyle: 'italic', fontSize: 28, color: CREAM }}>Thank you.</div>
        <p style={{ margin: '8px 0 16px', fontSize: 14, lineHeight: 1.65, color: 'rgba(245,242,237,0.85)', maxWidth: 560 }}>
          We have received your request and will get back to you as soon as possible{user.email ? ` at ${user.email}` : ''}.
        </p>
        <button type="button" onClick={() => setStatus('idle')} style={{
          background: 'transparent', color: CREAM, border: '1px solid rgba(245,242,237,0.6)',
          padding: '14px 24px', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
          cursor: 'pointer', fontFamily: SANS,
        }}>Send another request</button>
      </div>
    );
  }

  return (
    <form
      onSubmit={e => { e.preventDefault(); send(); }}
      style={{ flex: '1 1 560px', maxWidth: 880, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <label htmlFor="hero-request" style={{ fontFamily: "'Forum', serif", fontStyle: 'italic', fontSize: 'clamp(22px, 2.4vw, 28px)', color: CREAM }}>
        How can we help you?
      </label>
      <div className="flex flex-col sm:flex-row" style={{
        background: 'rgba(24,25,27,0.45)', border: '1px solid rgba(245,242,237,0.45)',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
      }}>
        <textarea
          id="hero-request"
          ref={textareaRef}
          rows={1}
          value={message}
          maxLength={5000}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={e => {
            // isComposing: Enter that confirms IME input (Japanese, Chinese…) must not send.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
          }}
          placeholder="A suite in Rome, a yacht in July…"
          className="placeholder:text-[rgba(245,242,237,0.6)]"
          style={{
            flex: '1 1 auto', minWidth: 0, resize: 'none', border: 'none', outline: 'none',
            background: 'transparent', color: CREAM, padding: '20px 24px',
            fontFamily: SANS, fontSize: 16, lineHeight: 1.5, overflowY: 'auto',
          }}
        />
        <button
          type="submit"
          disabled={!message.trim() || status === 'sending'}
          style={{
            flexShrink: 0, border: 'none', background: CREAM, color: INK, padding: '18px 30px',
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontFamily: SANS,
            cursor: status === 'sending' ? 'wait' : 'pointer',
            opacity: !message.trim() || status === 'sending' ? 0.85 : 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {status === 'sending' ? <><Loader2 size={12} className="animate-spin" /> Sending…</> : 'Send request ⟶'}
        </button>
      </div>
      {error && <p role="alert" style={{ margin: 0, fontSize: 12, color: '#ffb4ab', fontFamily: SANS }}>{error}</p>}
      <p style={{ margin: '8px 0 0', fontSize: 12, color: 'rgba(245,242,237,0.75)', fontFamily: SANS }}>
        Goes straight to our advisors — your details are included.
      </p>
    </form>
  );
}
